// src/pages/GeneratorUgovora.js
import React, { useState } from 'react';

// --- STILOVI ---
const styles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2rem',
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  formColumn: {
    flex: '1 1 400px',
    minWidth: '300px',
  },
  previewColumn: {
    flex: '1 1 500px',
    minWidth: '300px',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '1.5rem',
    textAlign: 'center',
  },
  contractPaper: {
    background: '#fff',
    color: '#000',
    padding: '40px',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    fontFamily: "'Times New Roman', Times, serif",
    lineHeight: '1.8',
    fontSize: '12pt',
    border: '1px solid #ddd',
  },
  contractTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '14pt',
    marginBottom: '20px',
    textTransform: 'uppercase',
  },
  paragraph: {
    textAlign: 'justify',
    marginBottom: '10px',
  },
  signature: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '60px',
    paddingTop: '20px',
  },
  printButton: {
    display: 'block',
    width: '100%',
    padding: '15px',
    backgroundColor: '#0984e3',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.2rem',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  disclaimer: {
    padding: '10px',
    backgroundColor: '#ffeaa7',
    color: '#6c5ce7',
    borderRadius: '6px',
    fontSize: '0.9rem',
    marginBottom: '20px',
    textAlign: 'center',
  },
  sectionTitle: {
    borderBottom: '1px solid #ccc',
    paddingBottom: '5px',
    marginTop: '20px',
    marginBottom: '10px',
    fontWeight: 'bold',
    color: '#555'
  }
};

// --- POMOĆNE FUNKCIJE ---
const v = (val) => val || '________________________';

const formatDate = (dateStr) => {
  if (!dateStr) return '________________________';
  const date = new Date(dateStr);
  return date.toLocaleDateString('sr-Latn-BA', { year: 'numeric', month: 'long', day: 'numeric' });
};

// --- KOMPONENTA ---
export default function UgovoriGenerator() {
  
  // Zajedničko stanje za sve ugovore
  const [formData, setFormData] = useState({
    type: '',
    
    // Zajednički podaci
    employerName: '',
    employerAddress: '',
    employeeName: '',
    employeeAddress: '',
    schoolName: '',
    employeeTitle: '',
    contractDate: new Date().toISOString().split('T')[0],
    contractPlace: 'Banja Luka',
    
    // Podaci za "Standardne" ugovore (Rad, Probni)
    startDate: '',
    endDate: '',
    jobTitle: '',
    workingHours: '8', // IZMJENA: Sada predstavlja dnevne sate
    weekType: 'petodnevnoj', // ISPRAVKA: Ispravljeno sa 'pethodnevnoj'
    shiftType: 'jednokratnim',
    salaryIncreasesText: '', 
    paymentDay: '',
    paymentFrequency: 'mjesečno',
    helpDeathWorker: '',
    helpDeathFamily: '',
    helpDisability: '',
    generalActs: '',
    
    // Specifično za PROBNI
    probationDuration: '',
    probationExtension: '',
    supervisorName: '',

    // Specifično za ODREĐENO
    reasonFixedTerm: '',

    // Specifično za NEODREĐENO
    noticePeriod: '',

    // Specifično za DOPUNSKI
    existingEmployer: '',
    durationType: 'odredjeno', 
    hoursPerWeek: '',
    compensationAmount: '',

    // Specifično za PP POSLOVI
    representativeName: '',
    durationDays: '',
    workLocation: '',
    netCompensation: '',
    courtLocation: '',

    // Specifično za USAVRŠAVANJE / OSPOSOBLJAVANJE
    rightsList: '',

    // Specifično za PRIPRAVNIČKI
    examCostsBearer: 'poslodavac',
    grossSalary: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrint = () => window.print();

  // --- POLJA FORME ---
  const renderFormFields = () => {
    const commonFields = (
      <>
        <div className="field-group">
          <label className="field-label">Naziv poslodavca *</label>
          <input type="text" name="employerName" value={formData.employerName} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Sjedište poslodavca</label>
          <input type="text" name="employerAddress" value={formData.employerAddress} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Ime i prezime radnika *</label>
          <input type="text" name="employeeName" value={formData.employeeName} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Adresa radnika</label>
          <input type="text" name="employeeAddress" value={formData.employeeAddress} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Završena škola/Fakultet</label>
          <input type="text" name="schoolName" value={formData.schoolName} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Stručno zvanje</label>
          <input type="text" name="employeeTitle" value={formData.employeeTitle} onChange={handleChange} className="field-input" />
        </div>
      </>
    );

    // Polja za plate i radno vrijeme (zajednička za ugovore o radu)
    const standardJobFields = (
      <>
        <div style={styles.sectionTitle}>Radno vrijeme i plata</div>
        
        <div className="field-group">
          <label className="field-label">Broj radnih sati dnevno</label>
          <input type="number" name="workingHours" value={formData.workingHours} onChange={handleChange} className="field-input" placeholder="npr. 8" />
        </div>

        <div className="field-group" style={{display: 'flex', gap: '10px'}}>
          <div style={{flex: 1}}>
            <label className="field-label">Radna sedmica</label>
            <select name="weekType" value={formData.weekType} onChange={handleChange} className="field-select">
              <option value="petodnevnoj">Petodnevnoj</option>
              <option value="šestodnevnoj">Šestodnevnoj</option>
            </select>
          </div>
          <div style={{flex: 1}}>
            <label className="field-label">Smjene</label>
            <select name="shiftType" value={formData.shiftType} onChange={handleChange} className="field-select">
              <option value="jednokratnim">Jednokratnim</option>
              <option value="dvokratnim">Dvokratnim</option>
              <option value="smjenskim">Smjenskim</option>
            </select>
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Iznos bruto plate (KM)</label>
          <input type="text" name="grossSalary" value={formData.grossSalary} onChange={handleChange} className="field-input" placeholder="npr. 1.500,00" />
        </div>
        
        <div className="field-group" style={{display: 'flex', gap: '10px'}}>
           <div style={{flex: 1}}>
            <label className="field-label">Isplata plate</label>
            <select name="paymentFrequency" value={formData.paymentFrequency} onChange={handleChange} className="field-select">
              <option value="mjesečno">Mjesečno</option>
              <option value="sedmično">Sedmično</option>
              <option value="svakih 15 dana">Svakih 15 dana</option>
            </select>
           </div>
           <div style={{flex: 1}}>
            <label className="field-label">Dan isplate</label>
            <input type="text" name="paymentDay" value={formData.paymentDay} onChange={handleChange} className="field-input" placeholder="npr. 15." />
           </div>
        </div>

        <div style={styles.sectionTitle}>Ostalo</div>
        <div className="field-group">
          <label className="field-label">Opšti akti poslodavca</label>
          <input type="text" name="generalActs" value={formData.generalActs} onChange={handleChange} className="field-input" placeholder="Naziv akata" />
        </div>
      </>
    );

    let specificFields = null;

    switch (formData.type) {
      // --- SLUČAJ 1: PROBNI RAD ---
      case 'proba':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Trajanje probnog rada</label>
              <input type="text" name="probationDuration" value={formData.probationDuration} onChange={handleChange} className="field-input" placeholder="npr. 3 mjeseca" />
            </div>
            <div className="field-group">
              <label className="field-label">Datum početka</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Datum završetka</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Radno mjesto *</label>
              <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Lice za nadzor/ocjenu</label>
              <input type="text" name="supervisorName" value={formData.supervisorName} onChange={handleChange} className="field-input" />
            </div>
            {standardJobFields}
          </>
        );
        break;

      // --- SLUČAJ 2: NA ODREĐENO ---
      case 'odredjeno':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Razlog za ugovor na određeno vrijeme *</label>
              <textarea name="reasonFixedTerm" value={formData.reasonFixedTerm} onChange={handleChange} className="field-input" rows="2" placeholder="npr. Zamjena odsutnog radnika..."></textarea>
            </div>
            <div className="field-group">
              <label className="field-label">Datum početka</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Radno mjesto *</label>
              <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Datum završetka / Događaj prestanka</label>
              <input type="text" name="endDate" value={formData.endDate} onChange={handleChange} className="field-input" placeholder="Datum ili opis događaja" />
            </div>
            {standardJobFields}
          </>
        );
        break;

      // --- SLUČAJ 3: NA NEODREĐENO ---
      case 'neodredjeno':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Datum stupanja u radni odnos</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Radno mjesto *</label>
              <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="field-input" />
            </div>
            {standardJobFields}
            <div className="field-group">
              <label className="field-label">Otkazni rok (dana)</label>
              <input type="text" name="noticePeriod" value={formData.noticePeriod} onChange={handleChange} className="field-input" placeholder="min 30" />
            </div>
          </>
        );
        break;

      // --- SLUČAJ 4: DOPUNSKI RAD ---
      case 'dopunski':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Naziv poslodavca kod koga je radnik već zaposlen</label>
              <input type="text" name="existingEmployer" value={formData.existingEmployer} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Datum stupanja na rad</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Opis poslova / Radno mjesto</label>
              <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Trajanje ugovora</label>
              <select name="durationType" value={formData.durationType} onChange={handleChange} className="field-select">
                <option value="odredjeno">Do određenog datuma</option>
                <option value="neodredjeno">Na neodređeno vrijeme</option>
              </select>
              {formData.durationType === 'odredjeno' && (
                <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="field-input" style={{marginTop: '5px'}}/>
              )}
            </div>
            <div className="field-group">
              <label className="field-label">Radno vrijeme (časova sedmično, max 20)</label>
              <input type="number" name="hoursPerWeek" value={formData.hoursPerWeek} onChange={handleChange} className="field-input" placeholder="npr. 10" />
            </div>
            <div className="field-group">
              <label className="field-label">Iznos naknade (KM)</label>
              <input type="text" name="compensationAmount" value={formData.compensationAmount} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Lice koje vrši nadzor</label>
              <input type="text" name="supervisorName" value={formData.supervisorName} onChange={handleChange} className="field-input" />
            </div>
          </>
        );
        break;

      // --- SLUČAJ 5: PP POSLOVI ---
      case 'pp_poslovi':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Lice ovlašteno za zastupanje</label>
              <input type="text" name="representativeName" value={formData.representativeName} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Opis poslova (PP poslovi do 90 dana)</label>
              <textarea name="jobTitle" value={formData.jobTitle} onChange={handleChange} className="field-input" rows="3"></textarea>
            </div>
            <div className="field-group">
              <label className="field-label">Rok za izvršenje (dana)</label>
              <input type="number" name="durationDays" value={formData.durationDays} onChange={handleChange} className="field-input" />
            </div>
             <div className="field-group">
              <label className="field-label">Datum završetka (alternativno)</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Mjesto izvršenja posla</label>
              <input type="text" name="workLocation" value={formData.workLocation} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Naknada (neto iznos KM)</label>
              <input type="text" name="netCompensation" value={formData.netCompensation} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Nadlezan sud (mjesto)</label>
              <input type="text" name="courtLocation" value={formData.courtLocation} onChange={handleChange} className="field-input" placeholder="npr. Banjoj Luci" />
            </div>
          </>
        );
        break;

      // --- SLUČAJ 6: USAVRŠAVANJE / OSPOSOBLJAVANJE / PRIPRAVNIČKI ---
      case 'usavrsavanje':
      case 'osposobljavanje':
      case 'pripravnicki':
        specificFields = (
          <>
            <div className="field-group">
              <label className="field-label">Datum početka</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="field-input" />
            </div>
            <div className="field-group">
              <label className="field-label">Datum završetka</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="field-input" />
            </div>
            
            {formData.type === 'pripravnicki' && (
              <>
                <div className="field-group">
                  <label className="field-label">Iznos bruto plate (KM)</label>
                  <input type="text" name="grossSalary" value={formData.grossSalary} onChange={handleChange} className="field-input" />
                </div>
                <div className="field-group">
                  <label className="field-label">Troškove ispita snosi</label>
                  <select name="examCostsBearer" value={formData.examCostsBearer} onChange={handleChange} className="field-select">
                    <option value="poslodavac">Poslodavac</option>
                    <option value="pripravnik">Pripravnik</option>
                  </select>
                </div>
              </>
            )}

            <div className="field-group">
              <label className="field-label">Prava koja radnik ostvaruje</label>
              <textarea name="rightsList" value={formData.rightsList} onChange={handleChange} className="field-input" rows="2" placeholder="npr. pravo na godišnji odmor..."></textarea>
            </div>
          </>
        );
        break;

      default:
        specificFields = null;
    }

    return (
      <>
        {commonFields}
        {specificFields}
        
        <div className="field-group" style={{ marginTop: '20px' }}>
          <label className="field-label">Datum zaključenja ugovora</label>
          <input type="date" name="contractDate" value={formData.contractDate} onChange={handleChange} className="field-input" />
        </div>
        <div className="field-group">
          <label className="field-label">Mjesto zaključenja</label>
          <input type="text" name="contractPlace" value={formData.contractPlace} onChange={handleChange} className="field-input" />
        </div>
      </>
    );
  };

  // --- GENERISANJE UGOVORA (PDF) ---
  const renderContract = () => {
    if (!formData.type) return <p style={{ color: '#888', textAlign: 'center' }}>Izaberite tip ugovora da biste vidjeli pregled.</p>;

    const d = formData;
    
    // Zajedničko zaglavlje za ugovore o radu
    const headerStandard = (article) => (
      <>
        <p style={{ textAlign: 'center' }}>{d.employerName || '________________________'}</p>
        <p style={{ textAlign: 'center' }}>{d.employerAddress || '________________________'}</p>
        <br />
        <p style={{ textAlign: 'justify' }}>
          Na osnovu člana {article}. Zakona o radu („Službeni glasnik Republike Srpske“, br. 1/16, 66/18, 91/21 i 119/21) poslodavac {v(d.employerName)} (u daljem tekstu: poslodavac) i radnik {v(d.employeeName)} iz {v(d.employeeAddress)} sa završenom školom/fakultetom {v(d.schoolName)} i stručnim/naučnim nazivom {v(d.employeeTitle)} (u daljem tekstu: radnik), zaključuju:
        </p>
        <br />
      </>
    );

    // Zajedničko zaglavlje za van radnog odnosa
    const headerOther = (article) => (
      <>
        <p style={{ textAlign: 'center' }}>{d.employerName || '________________________'}</p>
        <p style={{ textAlign: 'center' }}>{d.employerAddress || '________________________'}</p>
        <br />
        <p style={{ textAlign: 'justify' }}>
          Na osnovu člana {article}. Zakona o radu („Službeni glasnik Republike Srpske“, br. 01/16 i 66/18),
        </p>
        <br />
        <p style={{ textAlign: 'justify' }}>
          Poslodavac {v(d.employerName)} (u daljem tekstu: poslodavac) i {v(d.employeeName)} iz {v(d.employeeAddress)} sa završenom školom/fakultetom {v(d.schoolName)} i stručnim/naučnim nazivom {v(d.employeeTitle)}, zaključuju:
        </p>
        <br />
      </>
    );

    const signature = (
       <>
        <p style={{marginTop: '20px'}}>Ovaj ugovor zaključen je dana {formatDate(d.contractDate)} godine u {v(d.contractPlace)}.</p>
        <div style={styles.signature}>
          <div style={{ textAlign: 'center', width: '45%' }}>
            <p>_______________________</p>
            <p><strong>RADNIK / IZVRŠILAC</strong></p>
          </div>
          <div style={{ textAlign: 'center', width: '45%' }}>
            <p>_______________________</p>
            <p><strong>ZA POSLODAVCA</strong></p>
          </div>
        </div>
       </>
    );

    // Šabloni za uvećanja i pomoći (koristi se u standardnim ugovorima)
    const increasesBlock = `
      ${v(d.salaryIncreasesText)} za svaku godinu radnog staza radnika,
      _______ % po osnovu prekovremenog rada,
      _______ % po osnovu rada na dan republičkog praznika,
      _______ % po osnovu noćnog rada,
      _______ % po osnovu pripravnosti,
      _______ % po osnovu rada na radnom mjestu sa otežanim uslovima rada,
      _______ % po osnovu radnog učinka.
    `;

    const helpBlock = `
      smrti radnika ${v(d.helpDeathWorker)} KM,
      smrti člana porodice ${v(d.helpDeathFamily)} KM,
      nastupanja teške invalidnosti ${v(d.helpDisability)} KM.
    `;

    // SWITCH GLAVNI
    switch (d.type) {
      case 'proba':
        return (
          <>
            {headerStandard('37')}
            <h1 style={styles.contractTitle}>UGOVOR O PROBNOM RADU</h1>
            <p style={styles.paragraph}>1. Ovim ugovorom uređuje se probni rad radnika kod poslodavca u trajanju od {v(d.probationDuration)}.</p>
            <p style={styles.paragraph}>2. Radnik stupa na probni rad {formatDate(d.startDate)}, a završava {formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>4. Radnik se za vrijeme probnog rada raspoređuje na radno mjesto {v(d.jobTitle)}.</p>
            <p style={styles.paragraph}>
              6. Radno vrijeme radnika iznosi {v(d.workingHours)} časova dnevno i raspoređuje se u {v(d.weekType)} radnoj sedmici sa {v(d.shiftType)} rasporedom radnog vremena.
            </p>
            <p style={styles.paragraph}>7. Za redovan rad radnik ostvaruje bruto platu u iznosu od {v(d.grossSalary)} KM mjesečno.</p>
            <p style={styles.paragraph}>8. Osnovna plata radnika uvećava se: {increasesBlock}</p>
            <p style={styles.paragraph}>9. Poslodavac će radniku platu isplaćivati {v(d.paymentFrequency)}, a najkasnije do {v(d.paymentDay)} u mjesecu.</p>
            <p style={styles.paragraph}>10. Radniku ili njegovoj porodici pripada pomoć u slučaju: {helpBlock}</p>
            <p style={styles.paragraph}>13. Stručni nadzor i praćenje rada vršiće {v(d.supervisorName)}.</p>
            {signature}
          </>
        );

      case 'odredjeno':
        return (
          <>
            {headerStandard('39')}
            <h1 style={styles.contractTitle}>UGOVOR O RADU NA ODREĐENO VRIJEME</h1>
            <p style={styles.paragraph}>1. Ovim ugovorom zasniva se radni odnos radi: {v(d.reasonFixedTerm)}.</p>
            <p style={styles.paragraph}>2. Radnik stupa u radni odnos {formatDate(d.startDate)} i raspoređuje se na radno mjesto {v(d.jobTitle)}, koje će obavljati do {v(d.endDate)}.</p>
            <p style={styles.paragraph}>
              3. Radno vrijeme radnika iznosi {v(d.workingHours)} časova dnevno i raspoređuje se u {v(d.weekType)} radnoj sedmici, sa {v(d.shiftType)} rasporedom.
            </p>
            <p style={styles.paragraph}>4. Za redovan rad radnik ostvaruje bruto platu u iznosu od {v(d.grossSalary)} KM mjesečno.</p>
            <p style={styles.paragraph}>5. Osnovna plata radnika uvećava se: {increasesBlock}</p>
            <p style={styles.paragraph}>6. Isplata plate: {v(d.paymentFrequency)}, najkasnije do {v(d.paymentDay)}.</p>
            <p style={styles.paragraph}>7. Pomoći: {helpBlock}</p>
            {signature}
          </>
        );

      case 'neodredjeno':
        return (
          <>
            {headerStandard('33')}
            <h1 style={styles.contractTitle}>UGOVOR O RADU NA NEODREĐENO VRIJEME</h1>
            <p style={styles.paragraph}>1. Ovim ugovorom zasniva se radni odnos na neodređeno vrijeme. Radnik stupa u radni odnos {formatDate(d.startDate)} i raspoređuje se na radno mjesto {v(d.jobTitle)}.</p>
            <p style={styles.paragraph}>
              2. Radno vrijeme radnika iznosi {v(d.workingHours)} časova dnevno i raspoređuje se u {v(d.weekType)} radnoj sedmici, sa {v(d.shiftType)} rasporedom.
            </p>
            <p style={styles.paragraph}>3. Za redovan rad radnik ostvaruje bruto platu u iznosu od {v(d.grossSalary)} KM mjesečno.</p>
            <p style={styles.paragraph}>4. Osnovna plata radnika uvećava se: {increasesBlock}</p>
            <p style={styles.paragraph}>5. Isplata plate: {v(d.paymentFrequency)}, najkasnije do {v(d.paymentDay)}.</p>
            <p style={styles.paragraph}>6. Pomoći: {helpBlock}</p>
            <p style={styles.paragraph}>9. U slučaju otkaza... radnik ima pravo na otkazni rok od {v(d.noticePeriod)} dana.</p>
            {signature}
          </>
        );

      case 'dopunski':
        return (
          <>
            {headerOther('207')}
            <h1 style={styles.contractTitle}>UGOVOR O DOPUNSKOM RADU</h1>
            <p style={styles.paragraph}>1. Izvršilac posla, koji je zaposlen sa punim radnim vremenom kod {v(d.existingEmployer)} se prima na dopunski rad kod poslodavca sa danom {formatDate(d.startDate)}.</p>
            <p style={styles.paragraph}>2. Izvršilac posla se obavezuje da će obavljati poslove: {v(d.jobTitle)}.</p>
            <p style={styles.paragraph}>3. Poslove će obavljati do {d.durationType === 'neodredjeno' ? 'neodređeno' : formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>4. Izvršilac posla će raditi {v(d.hoursPerWeek)} časova sedmično.</p>
            <p style={styles.paragraph}>5. Naknada: {v(d.compensationAmount)} KM.</p>
            <p style={styles.paragraph}>8. Stručni nadzor vršiće {v(d.supervisorName)}.</p>
            {signature}
          </>
        );
      
      case 'pp_poslovi':
        return (
          <>
            {headerOther('204')}
            <p style={{ textAlign: 'justify' }}>
              ...{v(d.employerName)}, sa sjedištem u {v(d.employerAddress)}, a kojeg zastupa {v(d.representativeName)} (u daljem tekstu: narucilac posla) - s jedne strane, i {v(d.employeeName)} (u daljem tekstu: izvršilac posla) - s druge strane, zaključili su:
            </p>
            <h1 style={styles.contractTitle}>UGOVOR O OBAVLJANJU POVREMENIH I PRIVREMENIH POSLOVA</h1>
            <p style={styles.paragraph}>1. Izvršilac posla preuzima obavezu da obavi: {v(d.jobTitle)}.</p>
            <p style={styles.paragraph}>2. Rok izvršenja: {v(d.durationDays)} dana, odnosno do {formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>3. Mjesto izvršenja: {v(d.workLocation)}.</p>
            <p style={styles.paragraph}>5. Naknada: {v(d.netCompensation)} KM.</p>
            <p style={styles.paragraph}>6. Nadležan sud: Osnovni sud u {v(d.courtLocation)}.</p>
            {signature}
          </>
        );

      case 'usavrsavanje':
        return (
           <>
            {headerOther('206 stav 3')}
            <h1 style={styles.contractTitle}>UGOVOR O STRUČNOM USAVRŠAVANJU</h1>
            <p style={styles.paragraph}>1. Lice na stručnom usavršavanju se prima na rad radi sticanja posebnih znanja...</p>
            <p style={styles.paragraph}>2. Usavršavanje će obaviti u periodu od {formatDate(d.startDate)} do {formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>3. Lice ostvaruje prava: {v(d.rightsList)}.</p>
            {signature}
           </>
        );
      
      case 'osposobljavanje':
        return (
           <>
            {headerOther('206 stav 1')}
            <h1 style={styles.contractTitle}>UGOVOR O STRUČNOM OSPOSOBLJAVANJU</h1>
            <p style={styles.paragraph}>1. Lice na stručnom osposobljavanju se prima na rad radi osposobljavanja za samostalan rad...</p>
            <p style={styles.paragraph}>2. Pripravnički стаж će obavljati u vremenu od {formatDate(d.startDate)} do {formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>3. Lice ostvaruje prava: {v(d.rightsList)}.</p>
            {signature}
           </>
        );

      case 'pripravnicki':
        return (
          <>
            {headerOther('48')}
            <h1 style={styles.contractTitle}>UGOVOR O OBAVLJANJU PRIPRAVNIČKOG STAZA</h1>
            <p style={styles.paragraph}>1. Pripravnik se prima na rad radi osposobljavanja...</p>
            <p style={styles.paragraph}>2. Pripravnički staz će obavljati u vremenu od {formatDate(d.startDate)} do {formatDate(d.endDate)}.</p>
            <p style={styles.paragraph}>3. Troškove prvog polaganja ispita snosi {v(d.examCostsBearer)}.</p>
            <p style={styles.paragraph}>4. Pripravnik ostvaruje naknadu u iznosu od {v(d.grossSalary)} KM.</p>
            <p style={styles.paragraph}>6. Opšti akti poslodavca: {v(d.generalActs)}.</p>
            {signature}
          </>
        );

      default:
        return <p style={{ color: '#888' }}>Greška u tipu ugovora.</p>;
    }
  };

  return (
    <div style={styles.container}>
      {/* FORMA */}
      <div style={styles.formColumn} className="no-print">
        <h2 style={styles.title}>Generator Ugovora</h2>
        
        <div style={styles.disclaimer}>
          ⚠️ Pravna napomena: Ovo je još uvijek radna verzija i nije u potpunosti testirana. Provjerite podatke prije štampanja.
        </div>

        <div className="card">
          <div className="field-group">
            <label className="field-label">Izaberite tip ugovora *</label>
            <select name="type" value={formData.type} onChange={handleChange} className="field-select">
              <option value="">-- Tip ugovora --</option>
              <option value="neodredjeno">Ugovor o radu (Na neodređeno vrijeme)</option>
              <option value="odredjeno">Ugovor o radu (Na određeno vrijeme)</option>
              <option value="proba">Ugovor o probnom radu</option>
              <option value="dopunski">Ugovor o dopunskom radu</option>
              <option value="pp_poslovi">Ugovor o PP poslovima (Povremeni i privremeni)</option>
              <option value="usavrsavanje">Ugovor o stručnom usavršavanju</option>
              <option value="osposobljavanje">Ugovor o stručnom osposobljavanju</option>
              <option value="pripravnicki">Ugovor o obavljanju pripravničkog staza</option>
            </select>
          </div>

          {formData.type && renderFormFields()}
        </div>
      </div>

      {/* PREVIEW */}
      <div style={styles.previewColumn}>
        <button onClick={handlePrint} style={styles.printButton} className="no-print">
          🖨️ Štampaj / Sačuvaj PDF
        </button>

        <div className="print-area" style={styles.contractPaper}>
          {renderContract()}
        </div>
      </div>
    </div>
  );
}
